import { useEffect, useState } from "react";
import { groupKeyId, listGrantGroups, listGrantViewers } from "../../lib/accessClient.js";
import { ACTIVITY_CAPABILITIES } from "../../lib/activityConfiguration.js";

export const BUTTON = "rounded-[2px] border border-[color:var(--line)] px-3 py-2 text-[12px] disabled:opacity-50 cursor-pointer";
/// A destructive confirmation weights the safe choice, not the dangerous one:
/// STRONG is the way out, QUIET commits the deletion.
export const BUTTON_STRONG = "rounded-[2px] border border-[color:var(--ink)] bg-[color:var(--ink)] px-3 py-2 text-[12px] font-medium text-[color:var(--bg)] disabled:opacity-50 cursor-pointer";
export const BUTTON_QUIET = "rounded-[2px] border border-[color:var(--line)] px-3 py-2 text-[12px] text-[color:var(--muted)] disabled:opacity-50 cursor-pointer";
export const INPUT = "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-2 text-[12px]";
export const PANEL = "grid gap-3 rounded-[2px] border border-[color:var(--line)] p-4";
export const ALL_ACTIVITY = "All tenant activity, including former employees and unmapped records";
export const labelFor = capability => ACTIVITY_CAPABILITIES.find(item => item.value === capability)?.label ?? "Unknown capability";
const PROVIDERS = { okta: "Okta", entra_id: "Entra ID", google_workspace: "Google Workspace" };
export const groupName = group => `${group.displayName || "Directory group (name unavailable)"} (${PROVIDERS[group.sourceProvider] || group.sourceProvider})`;
export const recipientName = recipient => recipient?.viewerUserId
    ? recipient.displayName || "Individual user (name unavailable)" : recipient?.directoryGroup ? groupName(recipient.directoryGroup) : "Unknown recipient";
export const scopeName = scope => scope?.allTenantActivity ? ALL_ACTIVITY
    : scope?.groups ? `Selected groups: ${scope.groups.groups.map(groupName).join(", ")}` : "Approved investigation target";
export const shortLabelFor = capability => ACTIVITY_CAPABILITIES.find(item => item.value === capability)?.short ?? "Unknown capability";
/// Groups sharing one recipient collapse under a single heading, so the key has
/// to separate a viewer from a directory group that happens to share an id.
export const recipientKey = recipient => recipient?.viewerUserId ? `viewer:${recipient.viewerUserId}`
    : recipient?.directoryGroup ? `group:${groupKeyId(recipient.directoryGroup)}` : "unknown";
/// A list row states the scope, not the whole membership. Names past `limit`
/// collapse to a count rather than wrapping the row over several lines. The
/// provider is dropped here: it repeats on every group in the row while the
/// recipient heading above still carries it.
export function compactScope(scope, limit = 3) {
    if (scope?.allTenantActivity) return "All tenant activity";
    const names = scope?.groups?.groups?.map(group => group.displayName || "Directory group (name unavailable)") ?? [];
    if (!names.length) return "Approved investigation target";
    return names.length <= limit ? names.join(", ") : `${names.slice(0, limit).join(", ")} +${names.length - limit}`;
}

export function CandidatePicker({ kind, title, selected, onSelect }) {
    const [search, setSearch] = useState("");
    const [pageToken, setPageToken] = useState("");
    const [result, setResult] = useState(null);
    const [retry, setRetry] = useState(0);
    const query = JSON.stringify([kind, search, pageToken, retry]);
    const page = result?.query === query ? result : null;
    useEffect(() => {
        let current = true;
        const timer = setTimeout(() => {
            const load = kind === "viewer" ? listGrantViewers : listGrantGroups;
            load({ search, pageToken, pageSize: 25, ...(kind === "viewer" ? {} : { primaryDirectoryOnly: true }) }).then(value => {
                if (current) setResult({ query, rows: value.viewers ?? value.groups, next: value.nextPageToken, degraded: value.degraded });
            }).catch(() => { if (current) setResult({ query, rows: [], error: true }); });
        }, 180);
        return () => { current = false; clearTimeout(timer); };
    }, [query, kind, search, pageToken]);
    return <div className="grid gap-2" aria-busy={!page}>
        <label className="grid gap-1">{title}
            <input className={INPUT} type="search" value={search} onChange={event => { setSearch(event.target.value); setPageToken(""); }} />
        </label>
        {kind !== "viewer" ? <p className="text-[12px] text-[color:var(--muted)]">Primary directory only</p> : null}
        {!page ? <p>Loading choices...</p> : null}
        {page?.error ? <p role="alert">Choices could not be loaded. <button type="button" className={BUTTON} onClick={() => setRetry(value => value + 1)}>Retry choices</button></p> : null}
        {page?.degraded ? <p role="status">Directory groups are unavailable. Try again after directory access is restored.</p> : null}
        {page && !page.error && !page.degraded && !page.rows.length ? <p>No matching {kind === "viewer" ? "users" : "groups in the primary directory"}.</p> : null}
        <div className="grid max-h-60 gap-1 overflow-y-auto">{page?.rows.map(row => {
            const id = kind === "viewer" ? row.viewerUserId : groupKeyId(row);
            const chosen = selected.has(id);
            return <div key={id} className="flex items-center gap-2"><button type="button" aria-pressed={chosen} onClick={() => onSelect(row)}
                className={`${BUTTON} flex-1 text-left ${chosen ? "border-[color:var(--green)] bg-[color:var(--green-soft)] font-medium" : ""}`}>
                {kind === "viewer" ? row.displayName || "Individual user (name unavailable)" : groupName(row)}
            </button></div>;
        })}</div>
        <div className="flex gap-2">
            {pageToken ? <button type="button" className={BUTTON} onClick={() => setPageToken("")}>First choices</button> : null}
            {page?.next ? <button type="button" className={BUTTON} onClick={() => setPageToken(page.next)}>Next choices</button> : null}
        </div>
    </div>;
}

export function keyOnly(group) {
    return { sourceProvider: group.sourceProvider, integrationId: group.integrationId, sourceId: group.sourceId };
}
